import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiBell, FiCheckCircle, FiX } from 'react-icons/fi';
import useNotificationStore from '@/stores/NotificationStore';
import './NotificationCenter.css';

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
  anchorEl?: HTMLElement | null;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose, anchorEl }) => {
  const navigate = useNavigate();
  const {
    notifications,
    loading,
    fetchNotifications,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification
  } = useNotificationStore();

  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications(20, 0);
    }
  }, [isOpen, fetchNotifications]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleNotificationClick = (notification: any) => {
    if (!notification.is_read) {
      markNotificationAsRead(notification.uuid);
    }

    if (notification.action_url) {
      window.location.href = notification.action_url;
      onClose();
    }
  };

  const handleDelete = (e: React.MouseEvent, uuid: string) => {
    e.stopPropagation();
    deleteNotification(uuid);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'marketing':
        return '📢';
      case 'system':
        return '⚙️';
      case 'announcement':
        return '📣';
      case 'instructor':
        return '👨‍🏫';
      case 'event':
        return '📅';
      case 'course':
        return '📚';
      case 'assignment':
        return '📋';
      default:
        return '🔔';
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  };

  const handleViewAll = () => {
    navigate('/notifications');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="notification-overlay" onClick={onClose}></div>
      <div className="notification-center" ref={containerRef}>
        <div className="notification-header">
          <h3>Notifications</h3>
          <div className="notification-header-actions">
            {notifications.some((n) => !n.is_read) && (
              <button onClick={markAllNotificationsAsRead} className="mark-all-btn" title="Mark all as read">
                <FiCheckCircle size={16} />
                Mark all read
              </button>
            )}
            <button onClick={onClose} className="close-btn">
              <FiX size={20} />
            </button>
          </div>
        </div>

        <div className="notification-list">
          {loading ? (
            <div className="notification-loading">Loading notifications...</div>
          ) : notifications.length === 0 ? (
            <div className="notification-empty">
              <FiBell size={48} />
              <p>No notifications yet</p>
              <small>You're all caught up!</small>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.uuid}
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-icon">{getNotificationIcon(notification.notification_type)}</div>
                <div className="notification-content">
                  <h4>{notification.title}</h4>
                  <p>{notification.message}</p>
                  <span className="notification-time">{formatTime(notification.created_at)}</span>
                </div>
                <div className="notification-actions">
                  {!notification.is_read && <div className="unread-indicator"></div>}
                  <button
                    className="delete-btn"
                    onClick={(e) => handleDelete(e, notification.uuid)}
                    title="Delete notification"
                  >
                    <FiX size={16} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {!loading && (
          <div className="notification-footer">
            <span className="view-all-link" onClick={handleViewAll}>
              View All Notifications
            </span>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationCenter;
